import React, { memo, useCallback, useMemo, useState } from 'react';
import { batch } from 'react-redux';
import { prop, isEmpty } from 'ramda';
import { useForm } from 'react-final-form';
import { useAppSelector } from 'js/store/hooks';
import getValidateFn from 'js/utils/Validation';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useGetErrorMessage } from 'js/hooks/server';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { Hidden } from 'js/components/common/Fields/Hidden';
import { useGetBirthCountries, useGetRegionsByCountryCode } from 'server';
import { convertPickerToISO } from 'js/components/common/Fields/DatePicker';
import { IBirthCountry } from 'js/hooks/socket/hooks/useGetBirthCountries/types';
import { convertInputDate } from 'js/components/common/Fields/DateInput/helpers';
import { ISO_DATE_FORMAT } from 'js/components/common/Fields/DateInput/constants';
import { ICountryRegion } from 'js/hooks/socket/hooks/useGetRegionsByCountryCode/types';

import * as helpers from './helpers';
import * as actions from '../../store/actions';
import { useValidationConfig } from '../../hooks';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../store';

import * as Types from './types';

export const PlaceOfBirthFieldBare: React.FC<Types.IPlaceOfBirthFieldConfig> = ({
    field,
    disabled,
    className,
    isFieldHidden,
    ChangeFormField
}) => {
    const formAPI = useForm();
    const isMounted = useIsMounted();
    const getErrorMessage = useGetErrorMessage();
    const dateFormat = useAppSelector(prop('dateFormat'));
    const [storedBirthCountries, setBirthCountries] = useState<IBirthCountry[]>([]);
    const [storedRegions, setRegions] = useState<ICountryRegion[]>([]);
    const [areCountriesLoading, setCountriesLoading] = useState(false);
    const [areRegionsLoading, setRegionsLoading] = useState(false);
    const [validityDate, setValidityDate] = useState<string | null>(null);
    const [selectedBirthProvince, setSelectedBirthProvince] = useState('');
    const dispatch = useDynamicReducerDispatch();
    const getBirthCountries = useGetBirthCountries();
    const getRegionsByCountryCode = useGetRegionsByCountryCode();
    const hiddenFields = useDynamicReducerSelector(prop('hiddenFields'));
    const { countryCondition, innerFields: innerFieldsRaw } = field;
    const validationConfig = useValidationConfig();
    const innerFields = useMemo(() => {
        return {
            dateOfBirth: innerFieldsRaw.dateOfBirth,
            countryOfBirth: innerFieldsRaw.countryOfBirth,
            ...innerFieldsRaw.birthProvince && { birthProvince: innerFieldsRaw.birthProvince },
            ...innerFieldsRaw.birthProvince && innerFieldsRaw.cityOfBirth && { cityOfBirth: innerFieldsRaw.cityOfBirth }
        };
    }, [innerFieldsRaw]);

    const addHiddenInnerField = useCallback((fieldName: string | undefined) => {
        fieldName && dispatch(actions.addHiddenField(fieldName));
    }, [dispatch]);

    const removeHiddenInnerField = useCallback((fieldName: string | undefined) => {
        fieldName && dispatch(actions.removeHiddenField(fieldName));
    }, [dispatch]);

    const formAPIChange = useCallback((name: string | undefined, value: string) => {
        name && formAPI.change(name, value);
    }, [formAPI]);

    const dateOfBirthField = innerFields.dateOfBirth;

    const countryOfBirthField = useMemo(() => ({
        ...innerFields.countryOfBirth,
        customList: helpers.birthCountriesToOptions(storedBirthCountries || [])
    }), [innerFields, storedBirthCountries]);

    const birthProvinceField = useMemo(() => ({
        ...innerFields.birthProvince && {
            ...innerFields.birthProvince,
            customList: helpers.countryRegionsToOptions(storedRegions || [])
        }
    }), [innerFields, storedRegions]);

    const cityOfBirthField = useMemo(() => ({
        ...innerFields.cityOfBirth && {
            ...innerFields.cityOfBirth,
            customList: helpers.countryCitiesToOptions(storedRegions, selectedBirthProvince)
        }
    }), [innerFields, selectedBirthProvince, storedRegions]);

    useEffectOnce(() => {
        addHiddenInnerField(countryOfBirthField.name);
        addHiddenInnerField(birthProvinceField.name);
        addHiddenInnerField(cityOfBirthField.name);
    }, [birthProvinceField, cityOfBirthField, countryOfBirthField, addHiddenInnerField]);

    const getRecordLevelValidation = useCallback((value: string): Promise<Record<string, string> | null> => {
        if (!dateOfBirthField?.name || !validationConfig[dateOfBirthField.name]) {
            return Promise.resolve(null);
        }

        const recordLevelValidate =
            getValidateFn({ validationConfig: { [dateOfBirthField.name]: validationConfig[dateOfBirthField.name] } });

        return recordLevelValidate({ [dateOfBirthField.name]: value });
    }, [dateOfBirthField, validationConfig]);

    const onBirthDateChanged = useCallback((
        data: { day: string; month: string; year: string } | { target: { value: string } }) => {

        const birthDate = 'target' in data
            // manual input
            ? convertInputDate({ value: data.target.value, fromFormat: dateFormat, toFormat: ISO_DATE_FORMAT })
            // picker input
            : convertPickerToISO(data);
        const [yyyy, mm, dd] = (birthDate || '').split('-');
        let isRecordLevelValid = false;

        setRegions([]);
        formAPI.batch(() => {
            formAPIChange('countryRegulationCode', '');
            formAPIChange(countryOfBirthField.name, '');
            formAPIChange(birthProvinceField.name, '');
            formAPIChange(cityOfBirthField.name, '');
            formAPIChange('cityOfficialCode', '');
        });

        (async () => {
            try {
                const recordLevelValidation = await getRecordLevelValidation(birthDate);

                isRecordLevelValid = Boolean(dateOfBirthField?.name && !recordLevelValidation?.[dateOfBirthField.name]);
                // eslint-disable-next-line no-empty
            } catch {}

            if (!dd || !mm || !yyyy || !isRecordLevelValid) {
                setValidityDate(null);

                batch(() => {
                    dispatch(actions.setFormInternalError(''));
                    addHiddenInnerField(countryOfBirthField.name);
                    addHiddenInnerField(birthProvinceField.name);
                    addHiddenInnerField(cityOfBirthField.name);
                });

                return;
            }

            setValidityDate(birthDate);
            setBirthCountries([]);
            setCountriesLoading(true);
            dispatch(actions.setFormInternalError(''));

            try {
                const { birthCountries } = await getBirthCountries(birthDate);

                if (!isMounted()) {
                    return;
                }

                setBirthCountries(birthCountries);

                removeHiddenInnerField(countryOfBirthField.name);
            } catch (error: any) {
                isMounted() && dispatch(actions.setFormInternalError(getErrorMessage(error)));
            } finally {
                isMounted() && setCountriesLoading(false);
            }
        })();
    }, [
        birthProvinceField,
        cityOfBirthField,
        countryOfBirthField,
        dateFormat,
        dispatch,
        addHiddenInnerField,
        removeHiddenInnerField,
        getErrorMessage,
        formAPI,
        formAPIChange,
        getBirthCountries,
        isMounted,
        dateOfBirthField,
        getRecordLevelValidation
    ]);

    const onCountryChanged = useCallback((event: { target: { value: string } }) => {
        if (isEmpty(birthProvinceField)) {
            return;
        }

        const countryCode = event.target.value;
        const birthCountry = storedBirthCountries.find(b => b.code === countryCode);

        formAPI.batch(() => {
            formAPIChange('countryRegulationCode', birthCountry?.regulationCode || '');
            formAPIChange(birthProvinceField.name, '');
            formAPIChange(cityOfBirthField.name, '');
            formAPIChange('cityOfficialCode', '');
        });

        batch(() => {
            dispatch(actions.setFormInternalError(''));
            addHiddenInnerField(birthProvinceField.name);
            addHiddenInnerField(cityOfBirthField.name);
        });

        if (!validityDate || countryCode !== countryCondition) {
            return;
        }

        setRegions([]);
        setRegionsLoading(true);

        (async () => {
            try {
                const { regionsByCountryCode } = await getRegionsByCountryCode({
                    validityDate,
                    countryCode
                });

                if (!isMounted()) {
                    return;
                }

                setRegions(regionsByCountryCode);

                removeHiddenInnerField(birthProvinceField.name);
            } catch (error: any) {
                isMounted() && dispatch(actions.setFormInternalError(getErrorMessage(error)));
            } finally {
                isMounted() && setRegionsLoading(false);
            }
        })();
    }, [
        birthProvinceField,
        cityOfBirthField,
        countryCondition,
        dispatch,
        addHiddenInnerField,
        removeHiddenInnerField,
        formAPI,
        formAPIChange,
        getErrorMessage,
        getRegionsByCountryCode,
        isMounted,
        storedBirthCountries,
        validityDate
    ]);

    const onBirthProvinceChange = useCallback((event: { target: { value: string } }) => {
        if (isEmpty(cityOfBirthField)) {
            return;
        }

        const provinceCode = event.target.value;

        formAPI.batch(() => {
            formAPIChange(cityOfBirthField.name, '');
            formAPIChange('cityOfficialCode', '');
        });

        setSelectedBirthProvince(provinceCode);
        removeHiddenInnerField(cityOfBirthField.name);
    }, [removeHiddenInnerField, formAPIChange, formAPI, cityOfBirthField]);

    const onCityOfBirthChange = useCallback((event: { target: { value: string } }) => {
         const [officialCode] = event.target.value.split('-');

        formAPIChange('cityOfficialCode', officialCode);
    }, [formAPIChange]);

    if (!dateOfBirthField) {
        return null;
    }

    const showCountryOfBirthField = !areCountriesLoading
        && Boolean(countryOfBirthField?.customList?.length)
        && countryOfBirthField?.name && !hiddenFields.includes(countryOfBirthField.name);

    const showBirthProvinceField = showCountryOfBirthField
        && Boolean(birthProvinceField?.customList?.length)
        && birthProvinceField?.name && !hiddenFields.includes(birthProvinceField.name);

    const showCityOfBirthField = showBirthProvinceField
        && Boolean(cityOfBirthField?.customList?.length)
        && cityOfBirthField?.name && !hiddenFields.includes(cityOfBirthField.name);

    return (
        <>
            {showCountryOfBirthField && <Hidden name="countryRegulationCode" />}
            {showCityOfBirthField && <Hidden name="cityOfficialCode" />}
            <ChangeFormField
                key={dateOfBirthField.name}
                disabled={disabled}
                className={className}
                field={dateOfBirthField}
                isFieldHidden={isFieldHidden}
                onChange={onBirthDateChanged}
            />
            {showCountryOfBirthField && (
                <ChangeFormField
                    key={countryOfBirthField.name}
                    className={className}
                    field={countryOfBirthField}
                    onChange={onCountryChanged}
                    isFieldHidden={isFieldHidden}
                    disabled={disabled || areRegionsLoading}
                />
            )}
            {showBirthProvinceField && (
                <ChangeFormField
                    key={birthProvinceField.name}
                    disabled={disabled}
                    className={className}
                    field={birthProvinceField}
                    isFieldHidden={isFieldHidden}
                    onChange={onBirthProvinceChange}
                />
            )}
            {showCityOfBirthField && (
                <ChangeFormField
                    key={cityOfBirthField.name}
                    disabled={disabled}
                    className={className}
                    field={cityOfBirthField}
                    isFieldHidden={isFieldHidden}
                    onChange={onCityOfBirthChange}
                />
            )}
        </>
    );
};

export const PlaceOfBirthField = memo(PlaceOfBirthFieldBare);
